<script lang="ts" setup>
import { useDrawingStore } from '@/store/drawing'
import {
  $addSignBlockItems,
  $copySignGlossaryFromSideToSide,
  $createMultipleSignBlocks,
  $deleteMultipleSignBlockItems,
  $deleteSignBlock,
  $updateMultipleSignBlockItems,
  $updateMultipleSignBlocks,
  $updateMultipleSignGlossaryRows,
  $updateSignBlock,
  $updateSignBlockItem
} from '@/api/signs'
import Draggable from 'vuedraggable'
import { useUserStore } from '@/store/user'
import { can } from '@layouts/plugins/casl'
import ability from '@/plugins/casl/ability'
import { useSnackbarStore } from '@/store/snackbar'

interface IProps {
  sign?: any,
  signSides?: number,
  signGlossaryRowsBySide?: any,
  isDrawing?: boolean,
  sides?: string[],
}

const props = withDefaults(defineProps<IProps>(), {
  sign: () => ({}),
  signSides: 1,
  signGlossaryRowsBySide: () => ({}),
  isDrawing: false,
  sides: () => (['A', 'B', 'C', 'D']),
})

interface Emit {
  (e: 'handleSignBlockData', value: any): void
  (e: 'getSignBlocks'): void
  (e: 'getActivityTimelineItems'): void
}

const emit = defineEmits<Emit>()

const drawingStore = useDrawingStore()
const userStore = useUserStore()
const snackbarStore = useSnackbarStore()

const alignmentOptions = [
  { value: 1, icon: 'tabler-align-left' },
  { value: 2, icon: 'tabler-align-center' },
  { value: 3, icon: 'tabler-align-right' }
]

const glossaryBlockLoading = ref<boolean>(false)

const showGlossaryItemDialog = ref<boolean>(false)
const glossaryItemDialogIsAddGlossaryItem = ref<boolean>(false)
const glossaryItemDialogIsUpdateGlossaryItem = ref<boolean>(false)
const glossaryItemDialogUpdateGlossaryItemTextFields = ref<('text_1' | 'text_2')[]>([])
const glossaryItemDialogUpdateGlossaryItemTextValues = ref<{text_1: string, text_2: string}>({text_1: '', text_2: ''})
const glossaryItemDialogUpdateGlossaryItemIsCustomText = ref<boolean>(false)
const glossaryItemDialogIsPictogramOnly = ref<boolean>(false)
const glossaryItemDialogIsAddPictogram = ref<boolean>(false)
const glossaryItemDialogIsAddPictogramItem = ref<boolean>(false)
const glossaryItemDialogIsAddItemWithRow = ref<boolean>(false)
const glossaryItemDialogSelectedSignGlossaryRowId = ref<number | null>(null)
const glossaryItemDialogSelectedSignBlockId = ref<number | null>(null)
const glossaryItemDialogSelectedSignBlockItemId = ref<number | null>(null)
const glossaryItemDialogSelectedPictogram = ref<any>(null)

// Edit Sign Glossary Row
const showSignGlossaryRowEditDialog = ref<boolean>(false)
const selectedSignGlossaryRowToEdit = ref({})

const isSelectMode = ref<boolean>(false)
const selectedSignBlockItemIds = ref<number[]>([])
const deleteSelectedSignBlockItemsLoading = ref<boolean>(false)

const currentTab = ref<number>(0)

const tabs = computed<number[]>(() => {
  // If Sign Reprint - show only selected reprint side
  if (props.sign?.is_reprint) {
    return props.sign?.reprint_side != null ? [props.sign.reprint_side] : []
  }

  const result: number[] = []

  if (props.sign?.type_info) {
    for (let i = 0; i <= props.sign.type_info.sides; i++) {
      result.push(i)
    }
  }

  return result
})

watch(
  tabs,
  availableTabs => {
    if (!availableTabs.length) return

    if (!availableTabs.includes(currentTab.value)) {
      currentTab.value = availableTabs[0]
    }
  },
  { immediate: true }
)

const currentSideNumber = computed(() => {
  return tabs.value.includes(currentTab.value)
    ? currentTab.value
    : tabs.value[0] ?? 0
})

const isShowGlossaryBlock = computed(() => {
  if (props.isDrawing) {
    return !drawingStore.offlineMode
  }
  return true
})

const isUserCanChangeGlossary = computed(() => {
  return ability.can('change', 'sign') && props.sign.is_active_campaign
})

const addSignBlock = (isPictogram = false, isRow = false, selectedSignGlossaryRowId = null) => {
  showGlossaryItemDialog.value = true
  if (isPictogram) {
    glossaryItemDialogIsAddPictogramItem.value = true
  }
  // Add Block to New Row
  if (isRow) {
    glossaryItemDialogIsAddItemWithRow.value = true
  }
  // Add Block to Existing Row
  else {
    glossaryItemDialogSelectedSignGlossaryRowId.value = selectedSignGlossaryRowId
  }
}

const updateSignBlockItem = async (isDeleteItem = false as boolean, data = {} as any, type = '' as string) => {
  const payload = {
    is_delete: isDeleteItem,
  } as any

  if (type === 'text') {
    const {
      selectedGlossaryItemsIds: glossaryItemsIds,
      selectedGlossaryFields: glossaryTextFields,
      isCustomTextGlossaryItem: isCustomGlossaryText,
      signBlockText1: customText1,
      signBlockText2: customText2
    } = data

    const [glossaryItemId] = glossaryItemsIds
    payload.glossary_item_id = glossaryItemId
    payload.glossary_text_fields = glossaryTextFields
    payload.is_custom_glossary_text = isCustomGlossaryText
    payload.glossary_custom_text_1 = customText1
    payload.glossary_custom_text_2 = customText2
  }
  else if (type === 'pictogram') {
    const {
      selectedGlossaryItemsIds: glossaryItemsIds,
    } = data

    const [glossaryItemId] = glossaryItemsIds
    payload.glossary_item_id = glossaryItemId
  }
  const selectedSignBlockItemId = glossaryItemDialogSelectedSignBlockItemId.value

  // If no selectedSignBlockPictogramId - return
  if (!selectedSignBlockItemId) {
    return
  }

  try {
    // return Sign
    const res = await $updateSignBlockItem(selectedSignBlockItemId, payload)
    handleSignBlockData(res.data)
  } catch (e) {
    console.log(e)
  }
}

const createSignBlocks = async (
  data: any,
  isEmpty = false as boolean,
  isRow = false as boolean,
  signGlossaryRowId = null as number | null
) => {
  const {
    selectedGlossaryItemsIds: glossaryItemsIds,
    selectedGlossaryFields: glossaryTextFields,
    isCustomTextGlossaryItem: isCustomGlossaryText,
    signBlockText1: customText1,
    signBlockText2: customText2
  } = data

  const payload = {} as any

  // Create SignGlossaryRow with SignBlock
  if (glossaryItemDialogIsAddItemWithRow.value || isRow) {
    payload.sign = props.sign.id
    payload.side_number = currentSideNumber.value
  }
  // Create SignBlock and add to the SignGlossaryRow
  else {
    payload.sign_glossary_row =
      signGlossaryRowId ? signGlossaryRowId : glossaryItemDialogSelectedSignGlossaryRowId.value
  }

  if (isEmpty) {
    payload.is_empty = true
  }
  else if (isCustomGlossaryText) {
    payload.is_custom_glossary_text = isCustomGlossaryText
    payload.glossary_custom_text_1 = customText1
    payload.glossary_custom_text_2 = customText2
    payload.glossary_items_ids = []
  }
  else {
    payload.glossary_items_ids = glossaryItemsIds
  }

  payload.glossary_text_fields = glossaryTextFields
  try {
    // return Sign
    const res = await $createMultipleSignBlocks(payload)
    handleSignBlockData(res.data)
  } catch (e) {
    console.log(e)
  }
}

// Track pending cross-block moves
const pendingItemMoves = ref<{itemId: string, targetBlockId: string, newIndex: number} | null>(null)

// Update Sign Block Items position numbers (including cross-block movement)
const updateSignBlockItemsOrder = async (
  evt: {
    moved?: { newIndex: number, oldIndex: number, element: any },
    added?: { element: any, newIndex: number },
    removed?: { element: any, oldIndex: number }
  },
  currentBlockId: string
) => {
  // If it's a simple move within the same block
  if (evt.moved) {
    const { element: movedItem } = evt.moved
    const allRows = [...props.signGlossaryRowsBySide[currentSideNumber.value]]

    // Find the current block
    const targetBlock = allRows
      .flatMap(row => row.sign_blocks)
      .find(block => block.id === currentBlockId)

    if (!targetBlock) return

    // Update position numbers for all items in target block
    const itemsToUpdate = targetBlock.items.map((item: any, idx: number) => ({
      id: item.id,
      position_number: idx
    }))

    try {
      const res = await $updateMultipleSignBlockItems(itemsToUpdate)
      handleSignBlockData(res.data)
    } catch (e: any) {
      console.log(e)
      const mes = e?.response?.data ? e?.response?.data : e?.message
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Error',
        text: `Failed to update item position: ${mes}`
      })
    }
    return
  }

  // If an item was added to this block (first part of cross-block move)
  if (evt.added) {
    pendingItemMoves.value = {
      itemId: evt.added.element.id,
      targetBlockId: currentBlockId,
      newIndex: evt.added.newIndex
    }
    return
  }

  // If an item was removed from a block (second part of cross-block move)
  if (evt.removed && pendingItemMoves.value?.itemId === evt.removed.element.id) {
    if (!pendingItemMoves.value) return

    const { itemId, targetBlockId, newIndex } = pendingItemMoves.value
    const allRows = [...props.signGlossaryRowsBySide[currentTab.value]]

    // Find source and target blocks
    const sourceBlock = allRows
      .flatMap(row => row.sign_blocks)
      .find(block =>
        block.items.some((item: any) => item.id === itemId)
      )
    const targetBlock = allRows
      .flatMap(row => row.sign_blocks)
      .find(block => block.id === targetBlockId)

    if (!sourceBlock || !targetBlock) {
      pendingItemMoves.value = null
      return
    }

    // Find the moved item
    const movedItem = sourceBlock.items.find((item: any) => item.id === itemId)
    if (!movedItem) {
      pendingItemMoves.value = null
      return
    }

    // Prepare the update payload
    const itemsToUpdate: {
      id: number
      position_number: number
      sign_block_id?: number
    }[] = [{
      id: movedItem.id,
      position_number: newIndex,
      sign_block_id: targetBlock.id
    }]

    // Also update positions of other items in target block
    targetBlock.items.forEach((item: any, idx: number) => {
      if (item.id !== movedItem.id) {
        itemsToUpdate.push({
          id: item.id,
          position_number: idx
        })
      }
    })

    try {
      const res = await $updateMultipleSignBlockItems(itemsToUpdate)
      handleSignBlockData(res.data)
      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Success',
        text: 'Item moved successfully'
      })
    } catch (e: any) {
      console.log(e)
      const mes = e?.response?.data ? e?.response?.data : e?.message
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Error',
        text: `Failed to move item: ${mes}`
      })
    } finally {
      pendingItemMoves.value = null
    }
  }
}

// Track pending cross-row moves
const pendingBlockMoves = ref<{blockId: string, targetRowId: string, newIndex: number} | null>(null)

// Update Sign Blocks position numbers (including cross-row movement)
const updateSignBlocksOrder = async (
  evt: {
    moved?: { newIndex: number, oldIndex: number, element: any },
    added?: { element: any, newIndex: number },
    removed?: { element: any, oldIndex: number }
  },
  currentRowId: string
) => {
  // If it's a simple move within the same row
  if (evt.moved) {
    const { element: movedBlock } = evt.moved
    const allRows = [...props.signGlossaryRowsBySide[currentTab.value]]
    const targetRow = allRows.find(row => row.id === currentRowId)

    if (!targetRow) return

    // Update position numbers for all blocks in target row
    const blocksToUpdate = targetRow.sign_blocks.map((block: any, idx: number) => ({
      id: block.id,
      position_number: idx
    }))

    try {
      const res = await $updateMultipleSignBlocks(blocksToUpdate)
      handleSignBlockData(res.data)
    } catch (e: any) {
      console.log(e)
    }
    return
  }

  // If a block was added to this row (first part of cross-row move)
  if (evt.added) {
    pendingBlockMoves.value = {
      blockId: evt.added.element.id,
      targetRowId: currentRowId,
      newIndex: evt.added.newIndex
    }
    return
  }

  // If a block was removed from a row (second part of cross-row move)
  if (evt.removed && pendingBlockMoves.value?.blockId === evt.removed.element.id) {
    // Null check
    if (!pendingBlockMoves.value) return

    const { blockId, targetRowId, newIndex } = pendingBlockMoves.value
    const allRows = [...props.signGlossaryRowsBySide[currentTab.value]]

    // Find source and target rows
    const sourceRow = allRows.find(row =>
      row.sign_blocks.some((b: any) => b.id === blockId)
    )
    const targetRow = allRows.find(row => row.id === targetRowId)

    if (!sourceRow || !targetRow) {
      pendingBlockMoves.value = null
      return
    }

    // Find the moved block
    const movedBlock = sourceRow.sign_blocks.find((b: any) => b.id === blockId)
    if (!movedBlock) {
      pendingBlockMoves.value = null
      return
    }

    // Prepare the update payload
    const blocksToUpdate: {
      id: number,
      position_number: number,
      sign_glossary_row_id?: number
    }[] = [{
      id: movedBlock.id,
      position_number: newIndex,
      sign_glossary_row_id: targetRow.id
    }]

    // Also update positions of other blocks in target row
    targetRow.sign_blocks.forEach((block: any, idx: number) => {
      if (block.id !== movedBlock.id) {
        blocksToUpdate.push({
          id: block.id,
          position_number: idx
        })
      }
    })

    try {
      const res = await $updateMultipleSignBlocks(blocksToUpdate)
      handleSignBlockData(res.data)
      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Success',
        text: 'Block moved successfully'
      })
    } catch (e: any) {
      console.log(e)
      const mes = e?.response?.data ? e?.response?.data : e?.message
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Error',
        text: `Oops, something went wrong. ${mes}`
      })
    } finally {
      pendingBlockMoves.value = null
    }
  }
}

// Delete selected Sign Block Items
const deleteMultipleSignBlockItems = async () => {
  deleteSelectedSignBlockItemsLoading.value = true
  const payload = {
    sign_block_item_ids: selectedSignBlockItemIds.value
  }
  try {
    // return Sign(from the first SignBlockItem sent)
    const res = await $deleteMultipleSignBlockItems(payload)
    handleSignBlockData(res.data)
  } catch (e) {
    console.log(e)
  } finally {
    selectedSignBlockItemIds.value = []
    deleteSelectedSignBlockItemsLoading.value = false
    isSelectMode.value = false
  }
}

// Update Sign Glossary Rows position numbers
const updateSignGlossaryRowsOrder = async () => {
  const signGlossaryRows = props.signGlossaryRowsBySide[currentSideNumber.value]
    .map((i: any, Idx: number) => ({ id: i.id, position_number: Idx + 1 }))

  try {
    // return Sign(from the first SignGlossaryRow sent)
    const res = await $updateMultipleSignGlossaryRows(signGlossaryRows)
    handleSignBlockData(res.data)
  } catch (e) {
    console.log(e)
  }
}

const handleSignBlockData = (data: any) => {
  emit('handleSignBlockData', data)
  emit('getActivityTimelineItems')
}

const onCloseGlossaryItemDialog = () => {
  glossaryItemDialogIsAddGlossaryItem.value = false
  glossaryItemDialogIsUpdateGlossaryItem.value = false
  glossaryItemDialogUpdateGlossaryItemTextFields.value = []
  glossaryItemDialogUpdateGlossaryItemTextValues.value = {text_1: '', text_2: ''}
  glossaryItemDialogUpdateGlossaryItemIsCustomText.value = false
  glossaryItemDialogIsPictogramOnly.value = false
  glossaryItemDialogIsAddPictogram.value = false
  glossaryItemDialogIsAddPictogramItem.value = false
  glossaryItemDialogIsAddItemWithRow.value = false
  glossaryItemDialogSelectedSignGlossaryRowId.value = null
  glossaryItemDialogSelectedSignBlockId.value = null
  glossaryItemDialogSelectedSignBlockItemId.value = null
  glossaryItemDialogSelectedPictogram.value = null
}

const onClickRowPositionNumber = (row: any) => {
  if (isUserCanChangeGlossary.value) {
    showSignGlossaryRowEditDialog.value = true
    selectedSignGlossaryRowToEdit.value = {...row}
  }
}

const onClickSignBlockItem = (signBlockItem: any) => {
  if (isUserCanChangeGlossary.value) {
    if (isSelectMode.value) {
      toggleBlockItemSelection(signBlockItem.id)
    } else {
      const signBlockItemType = signBlockItem.item_type === 1 ? 'text' : 'pictogram'

      if (signBlockItemType == 'text') {
        glossaryItemDialogIsUpdateGlossaryItem.value = true
        glossaryItemDialogUpdateGlossaryItemIsCustomText.value = signBlockItem?.is_custom_text || false
        if (signBlockItem.text_1) {
          glossaryItemDialogUpdateGlossaryItemTextFields.value.push('text_1')
          glossaryItemDialogUpdateGlossaryItemTextValues.value.text_1 = signBlockItem?.text_1 || ''
        } else {
          glossaryItemDialogUpdateGlossaryItemTextValues.value.text_1 = ''
        }
        if (signBlockItem.text_2) {
          glossaryItemDialogUpdateGlossaryItemTextFields.value.push('text_2')
          glossaryItemDialogUpdateGlossaryItemTextValues.value.text_2 = signBlockItem?.text_2 || ''
        } else {
          glossaryItemDialogUpdateGlossaryItemTextValues.value.text_2 = ''
        }
      }
      // type == 'pictogram'
      else {
        glossaryItemDialogIsPictogramOnly.value = true
        // change
        glossaryItemDialogIsAddPictogram.value = false

        glossaryItemDialogSelectedPictogram.value = signBlockItem
      }

      showGlossaryItemDialog.value = true
      // Sign Block Item ID
      glossaryItemDialogSelectedSignBlockItemId.value = signBlockItem.id
    }
  }
}

const onAddSignBlockItem = (signBlockId: number, type: string) => {
  if (isUserCanChangeGlossary.value) {
    if (type == 'text') {
      glossaryItemDialogIsAddGlossaryItem.value = true
    }
    // type == 'pictogram'
    else {
      glossaryItemDialogIsPictogramOnly.value = true
      // add
      glossaryItemDialogIsAddPictogram.value = true
    }
    showGlossaryItemDialog.value = true
    // Glossary Block ID
    glossaryItemDialogSelectedSignBlockId.value = signBlockId
  }
}

const addSignBlockItems = async (data: any, type: string) => {
  const signBlockId = glossaryItemDialogSelectedSignBlockId.value

  // If no signBlockId - return
  if (!signBlockId) {
    return
  }

  let payload = {
    sign_block_id: signBlockId,
  } as any

  if (type == 'text') {
    const {
      selectedGlossaryItemsIds: glossaryItemsIds,
      selectedGlossaryFields: glossaryTextFields,
      isCustomTextGlossaryItem: isCustomGlossaryText,
      signBlockText1: customText1,
      signBlockText2: customText2
    } = data

    if (isCustomGlossaryText) {
      payload.is_custom_glossary_text = isCustomGlossaryText
      payload.glossary_custom_text_1 = customText1
      payload.glossary_custom_text_2 = customText2
      payload.glossary_item_ids = []
    } else {
      payload.glossary_item_ids = glossaryItemsIds
    }

    payload.glossary_text_fields = glossaryTextFields
  }
  // type == 'pictogram'
  else {
    payload.glossary_item_ids = data
  }

  try {
    // return Sign
    const res = await $addSignBlockItems(payload)
    handleSignBlockData(res.data)
  } catch (e) {
    console.log(e)
  }
}

const deleteSignBlock = async (signBlockId: number) => {
  // If there is no ID - return
  if (!signBlockId) {
    return
  }

  try {
    // return Sign
    const res = await $deleteSignBlock(signBlockId)
    handleSignBlockData(res.data)
  } catch (e) {
    console.log(e)
  }
}

const onSelectAlignment = async (signBlockId: number, alignmentValue: number) => {
  try {
    await $updateSignBlock(signBlockId, { alignment: alignmentValue })
  } catch (e) {
    console.log(e)
  }
}

const calculatePictogramBlockWidth = (pictogramsCount: number | undefined) => {
  if (!pictogramsCount) return 0

  const MAX_VISIBLE = 4
  const SINGLE_WIDTH = 40

  const count = Math.min(pictogramsCount, MAX_VISIBLE)
  return `${count * SINGLE_WIDTH}px`
}

// Check if a block item is selected
const isBlockItemSelected = (blockItemId: number) => selectedSignBlockItemIds.value.includes(blockItemId)

// Toggle block item selection
const toggleBlockItemSelection = (blockItemId: number) => {
  const index = selectedSignBlockItemIds.value?.indexOf(blockItemId)
  if (index === -1) {
    selectedSignBlockItemIds.value.push(blockItemId)
  } else {
    selectedSignBlockItemIds.value.splice(index, 1)
  }
}

const getDisplayText = (item: any): string => {
  const { text_1, text_2 } = item
  return [text_1, text_2].filter(Boolean).join(' ')
}

const copyGlossaryFromAnotherSide = async (copyFromSideIndex: number) => {
  glossaryBlockLoading.value = true

  const currentSignId = props.sign?.id
  if (!currentSignId) {
    glossaryBlockLoading.value = false
    return
  }

  const payload = {
    current_sign_id: currentSignId,
    copy_from_side: copyFromSideIndex,
    copy_to_side: currentSideNumber.value
  }
  try {
    const res = await $copySignGlossaryFromSideToSide(payload)
    handleSignBlockData(res.data)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Sign Glossary copied successfully!'
    })
  } catch (e: any) {
    console.log(e)
    const mes = e?.response?.data ? e?.response?.data : e?.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    glossaryBlockLoading.value = false
  }
}
</script>

<template>
  <div
    v-if="isShowGlossaryBlock"
    class="position-relative"
  >
    <GlossaryItemDialog
      v-model="showGlossaryItemDialog"
      :glossary-group-id="sign?.campaign_glossary_group_id || null"
      :glossary-pictogram-catalogue-id="sign?.campaign_glossary_pictogram_catalogue_id || null"
      :is-add-glossary-item="glossaryItemDialogIsAddGlossaryItem"
      :is-update-glossary-item="glossaryItemDialogIsUpdateGlossaryItem"
      :update-glossary-item-text-fields="glossaryItemDialogUpdateGlossaryItemTextFields"
      :update-glossary-item-text-values="glossaryItemDialogUpdateGlossaryItemTextValues"
      :update-glossary-item-is-custom-text="glossaryItemDialogUpdateGlossaryItemIsCustomText"
      :is-add-pictogram="!!glossaryItemDialogIsAddPictogram"
      :is-add-pictogram-item="!!glossaryItemDialogIsAddPictogramItem"
      :is-pictogram-only="!!glossaryItemDialogIsPictogramOnly"
      :selected-pictogram="glossaryItemDialogSelectedPictogram"
      @onClose="onCloseGlossaryItemDialog"
      @onCreateSignBlocks="createSignBlocks($event)"
      @onAddGlossaryItems="addSignBlockItems($event, 'text')"
      @onAddPictograms="addSignBlockItems($event, 'pictogram')"
      @onUpdateGlossaryItem="updateSignBlockItem(false, $event, 'text')"
      @onUpdatePictogram="updateSignBlockItem(false, $event, 'pictogram')"
      @onDeleteBlockItem="updateSignBlockItem(true)"
    />
    <SignGlossaryRowEditDialog
      v-model="showSignGlossaryRowEditDialog"
      :sign-glossary-row="selectedSignGlossaryRowToEdit"
      @onSubmit="emit('getSignBlocks'); emit('getActivityTimelineItems')"
    />
    <VOverlay
      v-model="glossaryBlockLoading"
      persistent
      absolute
      contained
      class="align-center justify-center"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </VOverlay>
    <div
      v-if="isUserCanChangeGlossary"
      class="d-flex align-center flex-wrap justify-space-between"
    >
      <div class="d-flex align-center flex-wrap">
        <VBtn
          v-if="isSelectMode"
          :disabled="!!deleteSelectedSignBlockItemsLoading"
          size="x-small"
          class="ma-1 py-0 px-3"
          @click="isSelectMode = false; selectedSignBlockItemIds = []"
        >
          <VIcon icon="tabler-x" />
          Cancel
        </VBtn>
        <VBtn
          v-else
          size="x-small"
          class="ma-1 py-0 px-3"
          @click="isSelectMode = true"
        >
          <VIcon
            icon="tabler-click"
            class="mr-1"
          />
          Select
          <VMenu
            activator="parent"
            class="default-tooltip"
            close-delay="0"
            location="top center"
            offset="5"
            open-delay="100"
            open-on-hover
          >
            <span>Select Items to Delete</span>
          </VMenu>
        </VBtn>
        <VBtn
          v-if="isSelectMode"
          size="x-small"
          :disabled="!selectedSignBlockItemIds.length || !!deleteSelectedSignBlockItemsLoading"
          class="ma-1 py-0 px-3"
          @click="deleteMultipleSignBlockItems"
        >
          <VIcon icon="tabler-trash-x" />
          <span class="text-none">
            Delete
            {{ selectedSignBlockItemIds.length }}
            Item(s)
          </span>
          <VProgressCircular
            v-if="deleteSelectedSignBlockItemsLoading"
            class="ml-1"
            size="12"
            width="2"
            indeterminate
            color="white"
          />
        </VBtn>
      </div>
      <div class="d-flex align-center flex-wrap">
        <VBtn
          size="x-small"
          class="ma-1 py-0 px-3"
          @click="createSignBlocks({}, true, true)"
        >
          <VIcon icon="mdi-add"/>
          Row
        </VBtn>
      </div>
    </div>
    <VTabs
      v-model="currentTab"
      bg-color="transparent"
      grow
    >
      <VTab
        v-for="tabIndex in tabs"
        :key="tabIndex"
        :value="tabIndex"
      >
        Side {{ sides[tabIndex] }}
      </VTab>
    </VTabs>

    <VWindow v-model="currentTab">
      <VWindowItem
        v-for="tabIndex in tabs"
        :key="tabIndex"
        :value="tabIndex"
      >
        <VCard flat>
          <VCardText
            class="overflow-x-auto"
          >
            <VRow v-if="tabs.length > 1 && isUserCanChangeGlossary">
              <VCol class="px-0 pt-0">
                <div class="d-flex justify-end align-center">
                  <VMenu location="bottom center">
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        size="x-small"
                      >
                        Copy
                        <VIcon
                          icon="tabler-copy"
                          size="small"
                        />
                      </VBtn>
                    </template>
                    <VList class="ma-0 pa-0 add-sign-block-item-list">
                      <div class="text-body-2 text-center no-pointer-events">
                        From:
                      </div>
                      <VListItem
                        v-for="copyFromSideIndex in tabs.filter(i => i !== currentTab)"
                        class="ma-0 pa-0"
                        @click="copyGlossaryFromAnotherSide(copyFromSideIndex)"
                      >
                        <div class="text-body-2">
                          Side {{ sides[copyFromSideIndex] }}
                        </div>
                      </VListItem>
                    </VList>
                  </VMenu>
                </div>
              </VCol>
            </VRow>
            <template v-if="signGlossaryRowsBySide[tabIndex].length">
              <!-- Draggable Rows -->
              <Draggable
                :list="signGlossaryRowsBySide[tabIndex]"
                :disabled="!isUserCanChangeGlossary"
                ghost-class="ghost-draggable"
                item-key="id"
                tag="div"
                class="v-row"
                group="rows"
                @change="updateSignGlossaryRowsOrder"
              >
                <template #item="{ element: row }">
                  <VCol
                    cols="12"
                    class="pt-1"
                    :class="{ 'sign-glossary-bottom-divider': !!row.bottom_divider }"
                  >
                    <VRow class="d-flex align-center justify-space-between py-2">
                      <VChip
                        class="row-position-number-chip position-relative"
                        @click="onClickRowPositionNumber(row)"
                      >
                        {{ row.position_number }}
                        <VIcon
                          v-if="isUserCanChangeGlossary"
                          class="row-position-number-chip-icon"
                          icon="tabler-edit"
                        />
                        <VMenu
                          v-if="isUserCanChangeGlossary"
                          activator="parent"
                          class="default-tooltip"
                          close-delay="0"
                          location="top center"
                          offset="5"
                          open-delay="100"
                          open-on-hover
                        >
                          <span>Edit Row</span>
                        </VMenu>
                      </VChip>
                      <div
                        v-if="isUserCanChangeGlossary"
                        class="d-flex align-center flex-wrap justify-end"
                      >
                        <VBtn
                          size="x-small"
                          class="ma-1"
                          @click="createSignBlocks({}, true, false, row.id)"
                        >
                          <VIcon icon="mdi-add" size="small" />
                          Column
                        </VBtn>
                      </div>
                    </VRow>
                    <!-- Draggable Blocks inside Row -->
                    <Draggable
                      :list="row.sign_blocks"
                      :disabled="!isUserCanChangeGlossary"
                      ghost-class="ghost-draggable"
                      item-key="id"
                      tag="div"
                      class="v-row mt-1"
                      group="blocks"
                      @change="(evt) => updateSignBlocksOrder(evt, row.id)"
                    >
                      <template #item="{ element: block }">
                        <VCol
                          class="position-relative mx-0 px-0 py-7 border glossary-block-container d-flex align-center"
                          :class="{
                            'justify-start' : block.alignment === 1,
                            'justify-center' : block.alignment === 2,
                            'justify-end' : block.alignment === 3
                          }"
                        >
                          <!-- Top-left corner block -->
                          <div
                            v-if="!isSelectMode && isUserCanChangeGlossary"
                            class="glossary-alignment-toggle-block-wrapper glossary-block-content"
                          >
                            <VBtnToggle
                              v-model="block.alignment"
                              class="glossary-alignment-toggle-block"
                              color="primary"
                              density="compact"
                              mandatory
                              :rounded="false"
                              divided
                              @update:model-value="newVal => onSelectAlignment(block.id, newVal)"
                            >
                              <VBtn
                                v-for="(item, itemKey) in alignmentOptions"
                                :key="itemKey"
                                :value="item.value"
                                class="glossary-alignment-toggle-block-btn"
                                variant="outlined"
                                size=""
                              >
                                <VIcon :icon="item.icon" />
                              </VBtn>
                            </VBtnToggle>
                          </div>

                          <!-- Other table content (text and pictograms) -->
                          <!-- Draggable Items inside Block -->
                          <Draggable
                            :list="block.items"
                            :disabled="!isUserCanChangeGlossary"
                            ghost-class="ghost-draggable"
                            item-key="id"
                            tag="div"
                            class="d-inline-flex flex-wrap"
                            :class="{ 'empty-draggable-block': block.items.length === 0 }"
                            group="items"
                            @change="(evt) => updateSignBlockItemsOrder(evt, block.id)"
                          >
                            <template #item="{ element: blockItem }">
                              <div
                                class="position-relative mx-0 px-1 glossary-block-item-container"
                                :class="{
                                  'text-left' : block.alignment === 1,
                                  'text-center' : block.alignment === 2,
                                  'text-right' : block.alignment === 3,
                                  'selected-glossary-block-item': isBlockItemSelected(blockItem.id),
                                }"
                                @click.stop="onClickSignBlockItem(blockItem)"
                              >
                                <template v-if="blockItem.item_type === 1">
                                  <span
                                    :class="{'glossary-item-custom-text': blockItem?.is_custom_text}"
                                  >
                                    {{ getDisplayText(blockItem) }}
                                  </span>
                                </template>
                                <template v-else-if="blockItem.item_type === 2">
                                  <div
                                    style="width: 30px; height: 30px"
                                    class="d-flex align-center justify-center mx-0"
                                  >
                                    <img
                                      :src="blockItem?.pic"
                                      alt="icon"
                                      style="width: 100%; height: 100%; object-fit: contain"
                                    />
                                  </div>
                                </template>
                                <VMenu
                                  v-if="isUserCanChangeGlossary"
                                  activator="parent"
                                  class="default-tooltip"
                                  close-delay="0"
                                  location="top center"
                                  offset="5"
                                  open-delay="100"
                                  open-on-hover
                                >
                                  <span v-if="isSelectMode">
                                    {{ isBlockItemSelected(blockItem.id) ? 'Unselect' : 'Select' }}
                                  </span>
                                  <span v-else>
                                    Update {{ blockItem.item_type === 1 ? 'Text' : 'Pictogram' }}
                                  </span>
                                </VMenu>
                              </div>
                            </template>
                          </Draggable>

                          <!-- Add Pictogram -->
                          <div
                            v-if="!isSelectMode && isUserCanChangeGlossary"
                            class="add-pictogram-block-wrapper glossary-block-content"
                          >
                            <VMenu location="bottom">
                              <template #activator="{ props }">
                                <VSheet
                                  v-bind="props"
                                  :height="24"
                                  :width="24"
                                  border
                                  class="add-pictogram-block d-flex align-center justify-center"
                                  rounded
                                >
                                  <VIcon icon="mdi-plus" />
                                </VSheet>
                              </template>
                              <VList class="ma-0 pa-0 add-sign-block-item-list">
                                <VListItem
                                  class="ma-0 pa-0"
                                  @click="onAddSignBlockItem(block.id, 'pictogram')"
                                >
                                  <div class="text-body-2">
                                    Add Pictogram
                                  </div>
                                </VListItem>
                                <VListItem
                                  class="ma-0 pa-0"
                                  @click="onAddSignBlockItem(block.id, 'text')"
                                >
                                  <div class="text-body-2">
                                    Add Glossary Item
                                  </div>
                                </VListItem>
                              </VList>
                            </VMenu>
                          </div>

                          <!-- Edit Block -->
                          <div
                            v-if="!isSelectMode && isUserCanChangeGlossary"
                            class="edit-glossary-btn-wrapper glossary-block-content"
                          >
                            <VBtn
                              class="edit-glossary-btn rounded-lg"
                              density="compact"
                              icon="tabler-trash-x"
                              variant="elevated"
                              size=""
                              @click="deleteSignBlock(block?.id)"
                            />
                            <VMenu
                              activator="parent"
                              class="default-tooltip"
                              close-delay="0"
                              location="top center"
                              offset="5"
                              open-delay="100"
                              open-on-hover
                            >
                              <span>Delete</span>
                            </VMenu>
                          </div>
                        </VCol>
                      </template>
                    </Draggable>
                  </VCol>
                </template>
              </Draggable>
            </template>
            <VRow v-else>
              <VCol class="text-center">
                Glossary Items not found
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VWindowItem>
    </VWindow>
  </div>
</template>

<style lang="scss">
/* Empty block state */
.empty-draggable-block {
  width: 100%;
  height: 100%;
}

.row-position-number-chip {
  padding: 0 7px !important;
  height: 16px !important;
  .row-position-number-chip-icon {
    margin-left: 1px;
    font-size: 13px;
  }
}

.sign-glossary-bottom-divider {
  border-bottom: 10px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.glossary-block-content {
  opacity: 0; /* Initially hide content */
  pointer-events: none; /* Prevent interaction when hidden */
  transition: opacity 0.4s ease; /* Smooth transition */
}

.glossary-block-container:hover .glossary-block-content {
  opacity: 1; /* Fade in on hover */
  pointer-events: auto; /* Allow interaction when visible */
}

.glossary-block-item-container {
  display: flex;
  align-items: center;
  transition: background-color 0.3s ease; // Smooth transition
  cursor: pointer; // Default cursor
  &:hover {
    background-color: rgba(var(--v-border-color), var(--v-border-opacity)); // Light gray
  }

  &:active {
    background-color: transparent;
    cursor: grabbing; // Change cursor when actively dragging
  }
}

.glossary-alignment-toggle-block-wrapper {
  position: absolute;
  top: 5px;
  left: 0;
  z-index: 1;
}

.selected-glossary-block-item {
  background: rgba(var(--v-theme-primary), 0.2);
}

.glossary-alignment-toggle-block {
  display: flex;
  height: 20px !important;
}

.glossary-alignment-toggle-block-btn {
  padding-left: 4px;
  padding-right: 4px;
}

.add-pictogram-block-wrapper {
  position: absolute;
  top: 2.5px;
  right: 30px;
  z-index: 1;
  .add-pictogram-block {
    cursor: pointer;
  }
}

.add-sign-block-item-list {
  .v-list-item {
  }
}

.edit-glossary-btn-wrapper {
  position: absolute;
  top: 2.5px;
  right: 0;
  z-index: 1;
  .edit-glossary-btn {
    width: 24px;
    height: 24px;
  }
}

.add-pictogram-small-btn {
  width: 30px;
  height: 24px;
}

.add-glossary-item-small-btn {
  width: 36px;
  height: 24px;
}

.glossary-item-custom-text {
  font-weight: bold;
  color: orange;
}
</style>
